/**
 * AppContent: routing logic and page rendering.
 * Rendered inside AuthGuard + CatalogProvider in App.tsx.
 * Consumes catalog state via useCatalog() and handles hash-based navigation.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import CatalogPage from "./pages/CatalogPage";
import PluginDetailPage from "./pages/PluginDetailPage";
import type { AiSearchResponse } from "./lib/ai-mode-client";
import ContributeSkillPage from "./pages/ContributeSkillPage";
import AdminPage from "./pages/AdminPage";
import HelpPage from "./pages/HelpPage";
import MySkillsPage from "./pages/MySkillsPage";
import McpServersPage from "./pages/McpServersPage";
import McpServerDetailPage from "./pages/McpServerDetailPage";
import ThemeToggle from "./components/ThemeToggle";
import SideNav from "./components/SideNav";
import type { EditData } from "./pages/ContributeSkillPage";
import { useAuth } from "./lib/auth-context";
import { env, IS_DEV } from "./lib/env";
import { isMcpServersEnabled } from "./lib/feature-flags";
import { useCatalog, installKeyForPlugin, findPluginByInstallKey } from "./lib/catalog-context";
import { isContributedRecord } from "./lib/contributed";
import { fetchSkillTree, fetchSkillBlob, checkAdminStatus, legacyInstallKey } from "./lib/github-proxy-client";
import { reportError } from "./lib/newrelic";
import { trackPageView } from "./lib/gtm";
import type { ExtensionType, MarketplaceEntry, User } from "./types/index";

import {
  parseHash,
  routeToHash,
  type HelpSectionId,
  type McpHelpSectionId,
  type McpSort,
  type Route,
  helpTopicForSection,
} from "./lib/routing";
import type { NavItemId } from "./lib/nav-config";
import { resolvePageTitle, resolveViewName } from "./lib/page-title";
import { computeEffectiveBreadcrumbs } from "./lib/parent-plugin";
import styles from "./AppContent.module.css";

const HELP_BACK_LABELS = {
  catalog: "Back to Marketplace",
  detail: "Back to Detail",
  mcp: "Back to MCP Servers",
  mcpserver: "Back to Server",
} as const;

/** With MCP Servers off, its routes (and Help's MCP sections) fall back to what's there today. */
function gateRoute(route: Route): Route {
  if (isMcpServersEnabled()) return route;
  if (route.page === "mcp" || route.page === "mcpserver") return { page: "catalog" };
  if (route.page === "help" && (route.topic === "mcp" || (route.section && helpTopicForSection(route.section) === "mcp"))) {
    return { page: "help" };
  }
  return route;
}

export default function AppContent() {
  // Build-time flag (REACT_APP_MCP_SERVERS_ENABLED); read per render so tests can flip it.
  const mcpServersEnabled = isMcpServersEnabled();
  const { plugins, isLoading: catalogLoading, error: catalogError, refresh, findByInstallKey, parentIndex, removePlugin, adoptionMeta } = useCatalog();
  const { user: authUser, logout } = useAuth();

  const [route, setRoute] = useState<Route>(() => gateRoute(parseHash(window.location.hash)));
  const [catalogNavKey, setCatalogNavKey] = useState(0);
  // Remounts the MCP Servers catalog when its nav item is chosen, so it opens fresh
  // (no search) even when already on it: the search updates the URL with replaceState,
  // so the route alone can't tell.
  const [mcpNavKey, setMcpNavKey] = useState(0);

  // AI answer (#626) lifted OUT of CatalogPage so it survives navigating into a plugin detail
  // and back: the catalog unmounts on the detail route (see the render switch below), which
  // would otherwise throw away a paid-for 10-30s judgement even though browser-back restores
  // the query from the URL. AppContent stays mounted across every route, so it is the natural
  // home. CatalogPage still owns the per-query invalidation and dismiss. Inert in prod: the AI
  // UI is gated off (REACT_APP_AI_SEARCH_ENABLED), so these never populate.
  const [aiRequested, setAiRequested] = useState(false);
  const [aiResult, setAiResult] = useState<AiSearchResponse | null>(null);
  const [aiResultQuery, setAiResultQuery] = useState<string | null>(null);
  // A per-query AI answer must never ride onto a query the user never pointed AI at. Because the
  // state is lifted it no longer resets on CatalogPage's remount, so the ROBUST guarantee lives in
  // CatalogPage, not here: `aiRequested` only ever DISPLAYS a cached answer (restored on
  // browser-back when it matches the query), and only an explicit Ask AI / Try again gesture ever
  // fetches — see its fetch-arm guard and mount-restore. These AppContent resets are the EAGER
  // complement on the paths that build a fresh catalog Route directly (the full logo/"Marketplace"
  // reset, and the detail-page chips): they clear immediately so nothing lingers, but correctness
  // no longer depends on enumerating every navigation — CatalogPage catches the rest (e.g. a
  // browser-nav straight to a different catalog query, which routes through no handler here).
  const resetAiAnswer = useCallback(() => {
    setAiRequested(false);
    setAiResult(null);
    setAiResultQuery(null);
  }, []);

  const [devEmail, setDevEmail] = useState("");
  const [devName, setDevName] = useState("");

  const entraUser = env.REACT_APP_ENTRA_CLIENT_ID ? authUser : null;
  const devUser: User | null =
    IS_DEV && !entraUser && devEmail
      ? { id: "dev", email: devEmail, name: devName || devEmail }
      : null;
  const user = entraUser ?? devUser;

  const [isAdmin, setIsAdmin] = useState(false);

  // Where Help was opened from, when its back button should return there (history.back)
  // instead of resetting to the catalog: a skill's page, or an MCP page.
  const [helpOrigin, setHelpOrigin] = useState<"detail" | "mcp" | "mcpserver" | null>(null);
  // Remounts HelpPage so a help→help deep link applies. Not keyed on
  // route.section: handleHelpSectionOpen's replaceState leaves it stale.
  const [helpNavKey, setHelpNavKey] = useState(0);

  const [editData, setEditData] = useState<EditData | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");

  // The catalog installName of a skill the user just published or edited and
  // asked to view. The processor rebuilds marketplace.json asynchronously after
  // the commit, so the entry can briefly be absent from the loaded catalog.
  // Tracking it lets the detail page show a "processing" state instead of a false
  // "Not found" until it appears (issue #546).
  const [awaitingKey, setAwaitingKey] = useState<string | null>(null);

  // Scoped failure flag for the manual "Refresh" button in the processing state.
  // That refetch is silent (a failure must not flip the global catalog error), so
  // this gives the user-initiated action its own local feedback instead (#546).
  const [refreshFailed, setRefreshFailed] = useState(false);

  // Accessibility (§7.4): on in-app (hash) navigation, move focus to the top of
  // the main region and announce the new view via a polite live region, so
  // keyboard and screen-reader users are repositioned. Hash routing otherwise
  // leaves focus on the control that was activated and never tells assistive
  // tech that the page changed.
  const mainRef = useRef<HTMLElement>(null);
  const [routeAnnouncement, setRouteAnnouncement] = useState("");
  // Holds the title queued by the route-change effect while the live region is
  // momentarily cleared (see the clear-then-set effects below).
  const [pendingAnnouncement, setPendingAnnouncement] = useState<string | null>(null);

  useEffect(() => {
    if (user?.email) {
      checkAdminStatus().then(setIsAdmin).catch(() => setIsAdmin(false));
    }
  }, [user?.email]);

  const navigate = useCallback((next: Route) => {
    window.location.hash = routeToHash(next);
  }, []);

  // "View in Catalog" after a publish/edit. Remember the target so the detail
  // page can distinguish "just submitted, catalog still rebuilding" from a real
  // 404, and kick off a refetch in case the catalog has already caught up.
  const handleViewSkill = useCallback(
    (installName: string) => {
      setAwaitingKey(installName);
      setRefreshFailed(false);
      // Silent: a failure here must not flip the global catalog error (which would
      // wrongly report the catalog as down right after a successful publish). The
      // "processing" detail state is the fallback if the entry isn't there yet.
      void refresh({ silent: true });
      navigate({ page: "detail", installKey: installName });
    },
    [refresh, navigate],
  );

  // onHashChange fires the GA pageview synchronously, before React re-renders, so it
  // can't read selectedPlugin (derived during render). Resolve the plugin from a ref
  // here to give the pageview a meaningful title for in-app navigation; the title
  // effect below refines it once a deep-linked detail page's plugin resolves.
  const pluginsRef = useRef(plugins);
  pluginsRef.current = plugins;

  useEffect(() => {
    const onHashChange = () => {
      const next = gateRoute(parseHash(window.location.hash));
      if (next.page !== "help") setHelpOrigin(null);
      else setHelpNavKey((k) => k + 1);
      setRoute(next);
      // Use pluginsRef (always current) rather than the stale findByInstallKey closure —
      // the hashchange fires synchronously, before React re-renders, so the context
      // value captured at effect-setup time would return null for any plugin that
      // arrived after mount. Mirrors the old catalogRef pattern in App.tsx.
      const plugin =
        next.page === "detail" || next.page === "edit"
          ? findPluginByInstallKey(pluginsRef.current, next.installKey)
          : null;
      const title = resolvePageTitle(next, plugin);
      document.title = title;
      trackPageView(window.location.hash, title);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const editInstallKey = route.page === "edit" ? route.installKey : null;

  useEffect(() => {
    if (!editInstallKey) {
      setEditData(null);
      setEditError("");
      return;
    }

    const plugin = plugins.find(
      (p) => installKeyForPlugin(p).toLowerCase() === editInstallKey.toLowerCase(),
    );
    if (!plugin) {
      setEditError("Not found in catalog");
      return;
    }
    // Only contributed-skills records can be edited — the write path is bound to
    // that one repo (issue #404). Bail here so a hand-typed #edit/<foreign-key>
    // can't open the editor and clone/overwrite a foreign record on submit. The
    // detail page hides the Edit button via the same check; this guards the route.
    if (!isContributedRecord(plugin)) {
      setEditData(null);
      setEditError("This plug-in or skill can't be edited here — only skills contributed through the marketplace can be edited.");
      return;
    }

    let cancelled = false;
    setEditLoading(true);
    setEditError("");

    // github-proxy decodes source/org/repo from the legacy install-key shape;
    // the marketplace's installName (= manifestName post #176) isn't parseable.
    const proxyKey = legacyInstallKey(plugin);

    fetchSkillTree(proxyKey, plugin.lastCommitSha, plugin.skillPath)
      .then(async (tree) => {
        if (cancelled) return;
        const blobs = await Promise.all(
          tree.files.map(async (f) => {
            try {
              const blob = await fetchSkillBlob(proxyKey, f.sha);
              const content =
                blob.encoding === "base64"
                  ? blob.content
                  : btoa(
                      new TextEncoder()
                        .encode(blob.content)
                        .reduce((s, b) => s + String.fromCharCode(b), ""),
                    );
              return { name: f.path.split("/").pop()!, path: f.path, content };
            } catch (err) {
              // A single blob failing to load must not abort the whole edit-load, but
              // it must not vanish silently either — the file drops out of the editable
              // set and the contributor sees an incomplete list. Report it so a partial
              // load is observable (the file is still preserved server-side via the
              // base-tree merge; a dropped file can never enter deletedPaths).
              reportError(err, "edit-skill-blob-load");
              return null;
            }
          }),
        );
        if (cancelled) return;
        const validBlobs = blobs.filter((b): b is NonNullable<typeof b> => b !== null);

        let userKeywords = plugin.keywords;
        // Read keywords from the manifest BEFORE normalization/filtering below —
        // the raw tree path still carries the `.claude-plugin/` prefix here.
        const manifestBlob = validBlobs.find((b) => b.path.endsWith("plugin.json"));
        if (manifestBlob) {
          try {
            const manifest = JSON.parse(atob(manifestBlob.content));
            if (Array.isArray(manifest.keywords)) {
              userKeywords = manifest.keywords;
            }
          } catch {
            // fall back to catalog keywords
          }
        }

        // Normalize each path to be plugin-root-relative — the base the commit
        // builder re-prepends `skills/<slug>/` onto (issue #418). fetchSkillTree
        // strips only `skills/<slug>/`, so a NEW-layout skill's content already
        // arrives root-relative (`SKILL.md`, `references/…`) and only the manifest
        // carries `.claude-plugin/`. The strip's real job is to normalize that
        // manifest path to `plugin.json` so the filter below drops it; for new-layout
        // content it's a no-op (nothing else carries the prefix). A pre-fix LEGACY
        // skill arrives with its content nested under `.claude-plugin/`, which the
        // strip does un-nest — but editing is NOT the migration path: a same-slug edit
        // writes the un-nested copies ALONGSIDE the old nested originals (it never
        // deletes them), so the few legacy dirs are fixed out-of-band by a one-time
        // cleanup, not on save.
        const PLUGIN_ROOT = ".claude-plugin/";
        const existingFiles = validBlobs
          .map((b) => {
            const path = b.path.startsWith(PLUGIN_ROOT) ? b.path.slice(PLUGIN_ROOT.length) : b.path;
            // Re-derive name from the normalized path so name and path can't drift if
            // name's derivation ever changes (both are the basename today).
            return { ...b, path, name: path.split("/").pop()! };
          })
          // Drop the auto-generated manifest: upload.ts regenerates plugin.json, so a
          // kept one would re-upload at `plugin.json` and collide with the regenerated
          // entry in the same tree. Its content was always overwritten anyway.
          .filter((b) => b.path !== "plugin.json");

        setEditData({
          // installKey on editData is sent back to github-proxy as
          // `previousInstallKey` for rename detection, so use the legacy shape.
          installKey: proxyKey,
          skillName: plugin.pluginName,
          description: plugin.description,
          keywords: userKeywords,
          existingFiles,
        });
      })
      .catch((err: unknown) => {
        // Report before the cancelled guard so navigation-away errors
        // (e.g., a github-proxy 5xx during route change) still reach NR.
        reportError(err, "edit-skill-files-load");
        if (cancelled) return;
        setEditData(null);
        setEditError(err instanceof Error ? err.message : "Failed to load files");
      })
      .finally(() => {
        if (!cancelled) setEditLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editInstallKey, plugins]);

  const handleSelectPlugin = (plugin: MarketplaceEntry) => {
    navigate({ page: "detail", installKey: installKeyForPlugin(plugin) });
  };

  const handleBackToCatalog = useCallback(() => {
    const catalogRoute: Route = { page: "catalog" };
    setRoute(catalogRoute);
    navigate(catalogRoute);
    setCatalogNavKey((k) => k + 1);
    // Full reset (logo/home + the "Marketplace" breadcrumb) returns to a bare, unfiltered catalog,
    // so drop the lifted AI answer too — matching the fresh-catalog remount.
    resetAiAnswer();
  }, [navigate, resetAiAnswer]);

  // The edit page can be opened from either the catalog (detail → Edit) or the
  // My Skills page. When opened from My Skills, its back link returns there;
  // otherwise it defaults to the marketplace. Mirrors the HelpPage precedent.
  const editFromMyskills = route.page === "edit" && route.from === "myskills";
  const handleEditBack = useCallback(() => {
    if (editFromMyskills) navigate({ page: "myskills" });
    else handleBackToCatalog();
  }, [editFromMyskills, navigate, handleBackToCatalog]);

  // The trail is derived from the route (see computeEffectiveBreadcrumbs), so a
  // crumb click is just a navigation — there's no stack to slice. index is unused.
  const handleBreadcrumbClick = useCallback(
    (plugin: MarketplaceEntry) => {
      navigate({ page: "detail", installKey: installKeyForPlugin(plugin) });
    },
    [navigate],
  );

  const navigateToHelp = useCallback(() => {
    setHelpOrigin(route.page === "detail" ? "detail" : null);
    navigate({ page: "help" });
  }, [route.page, navigate]);

  // Help opened from an MCP page lands on the MCP topic (or one of its sections) and returns there.
  const navigateToMcpHelp = useCallback(
    (section?: McpHelpSectionId) => {
      setHelpOrigin(route.page === "mcp" || route.page === "mcpserver" ? route.page : null);
      navigate(section ? { page: "help", section } : { page: "help", topic: "mcp" });
    },
    [route.page, navigate],
  );

  // Side nav selection. Skills & Plugins does the same full reset as the logo (fresh,
  // unfiltered catalog); Help keeps its "Back to Detail" origin tracking.
  const handleNavSelect = useCallback(
    (id: NavItemId) => {
      switch (id) {
        case "skills":
          handleBackToCatalog();
          break;
        case "myskills":
          navigate({ page: "myskills" });
          break;
        case "contribute":
          navigate({ page: "contribute" });
          break;
        case "admin":
          navigate({ page: "admin" });
          break;
        case "help":
          navigateToHelp();
          break;
        case "help-mcp":
          setHelpOrigin(null);
          navigate({ page: "help", topic: "mcp" });
          break;
        case "mcp": {
          // Set the route too, as handleBackToCatalog does: search and sort update the URL with
          // replaceState, so `route` can still hold an old deep link's filters, and when the hash
          // is already "#/mcp" assigning it fires no hashchange to refresh it.
          const mcpRoute: Route = { page: "mcp" };
          setRoute(mcpRoute);
          setMcpNavKey((k) => k + 1);
          navigate(mcpRoute);
          break;
        }
      }
    },
    [handleBackToCatalog, navigate, navigateToHelp],
  );

  const replaceFilters = useCallback(
    (
      query: string,
      clients: string[],
      categories: string[],
      type?: ExtensionType,
      official?: boolean,
      // Multi-select author/org chips.
      identities?: string[],
    ) => {
      const hash = routeToHash({
        page: "catalog",
        filterClients: clients.length > 0 ? clients : undefined,
        filterCategories: categories.length > 0 ? categories : undefined,
        filterQuery: query || undefined,
        filterType: type,
        filterOfficial: official || undefined,
        filterIdentities: identities?.length ? identities : undefined,
      });
      window.history.replaceState(null, "", hash);
    },
    [],
  );

  const replaceMcpFilters = useCallback((query: string, sort: McpSort) => {
    window.history.replaceState(null, "", routeToHash({ page: "mcp", filterQuery: query || undefined, sort }));
  }, []);

  // replaceState, like replaceFilters: no history entry per section click.
  const handleHelpSectionOpen = useCallback((section: HelpSectionId) => {
    window.history.replaceState(null, "", routeToHash({ page: "help", section }));
  }, []);

  const handleLogout = () => {
    logout()
      .catch((error) => console.error("Logout failed:", error))
      .finally(() => window.location.reload());
  };

  const selectedPlugin =
    route.page === "detail" || route.page === "edit"
      ? findByInstallKey(route.installKey)
      : null;

  // Resolve the plugin a derived skill came from, so its detail page shows the
  // parent reference regardless of entry point (direct catalog click, deep link).
  const parentPlugin = selectedPlugin?.installName
    ? parentIndex.get(selectedPlugin.installName)
    : undefined;

  const effectiveBreadcrumbs = computeEffectiveBreadcrumbs(parentPlugin);

  // Drop the awaiting marker once it no longer applies. Deliberately NOT cleared
  // just because the current route isn't the detail page yet: navigate() sets the
  // hash synchronously, but the derived `route` state only updates asynchronously
  // when the resulting hashchange fires, so right after the "View in Catalog" click
  // `route` still reflects the origin (contribute/edit) page for a tick. Clearing
  // then would kill the marker before we ever land on the detail page.
  //
  // The "different target" check is scoped to `detail` only, NOT `edit`. A rename
  // changes the slug, so `awaitingKey` (the new slug) differs from the edit route's
  // installKey (the old slug); during the post-click tick the route is still
  // `edit/<old-slug>`, and treating that as a "different target" would clear the
  // marker a tick early and re-show a false "Not found" for the renamed skill (the
  // exact #546 symptom). `handleViewSkill` always navigates to a `detail` page, so a
  // lingering `edit` route is only ever that transient and is safe to ignore here.
  // Clear only when (a) the awaited entry has resolved (so a later genuine 404 for
  // the same key isn't masked), or (b) the user opened a DIFFERENT skill's detail page.
  useEffect(() => {
    if (!awaitingKey) return;
    const onThisDetail =
      route.page === "detail" && route.installKey.toLowerCase() === awaitingKey.toLowerCase();
    const onDifferentDetail =
      route.page === "detail" &&
      route.installKey.toLowerCase() !== awaitingKey.toLowerCase();
    if ((onThisDetail && selectedPlugin) || onDifferentDetail) {
      setAwaitingKey(null);
    }
  }, [awaitingKey, route, selectedPlugin]);

  // Keep the document/tab title in sync with the active route. selectedPlugin
  // resolves asynchronously after the catalog loads, so a deep link to a detail
  // page relies on this to correct the title once the plugin is known.
  // onHashChange owns the GA pageview; this effect only refines the title.
  const pageTitle = resolvePageTitle(route, selectedPlugin);
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  // A view change is a page transition (catalog/detail/edit/contribute/help/admin/myskills),
  // including detail→detail for a different plugin. Catalog filter changes use
  // history.replaceState and don't alter `route`, so they don't reposition focus.
  const viewKey =
    route.page === "detail" || route.page === "edit"
      ? `${route.page}:${route.installKey}`
      : route.page;

  // The live region announces the view-specific name (no "AI Marketplace - "
  // prefix) so screen-reader users hear what changed, not the product name on
  // every navigation. document.title keeps the full prefixed title (above).
  const viewName = resolveViewName(route, selectedPlugin);
  // Read the latest view name inside the focus effect without making it a
  // dependency (mirrors pluginsRef): the effect should fire on view change, not
  // on every async title refinement.
  const viewNameRef = useRef(viewName);
  viewNameRef.current = viewName;

  // prevViewKey starts equal to the first view, so the effect no-ops on initial
  // mount (no focus-stealing on first paint) and is resilient to StrictMode's
  // dev-only double-invoke; it acts only when the view genuinely changes.
  const prevViewKey = useRef(viewKey);
  useEffect(() => {
    if (prevViewKey.current === viewKey) return;
    prevViewKey.current = viewKey;
    // A new view starts at the top of the window, header included. preventScroll because
    // focusing a <main> taller than the window scrolls it to the window's top edge, which
    // pushed the header out of view on every navigation to a long page (the catalog, My
    // Skills). HelpPage's ?section= scroll runs a frame later, so it still lands.
    window.scrollTo(0, 0);
    mainRef.current?.focus({ preventScroll: true });
    // Clear the live region, then set the new title once the cleared state has
    // committed (the effect below). A polite region only re-announces when its
    // text node actually changes, and setting the *same* string bails out of
    // React's render — so two consecutive views with identical titles (two
    // "Not Found" details, or two different repos whose plugins share a name)
    // would reposition focus silently. Routing through "" forces a real text
    // change every time. (Re-announcement still warrants a manual NVDA/VoiceOver
    // check — jsdom can't observe the spoken output.)
    setRouteAnnouncement("");
    setPendingAnnouncement(viewNameRef.current);
  }, [viewKey]);

  // Second half of the clear-then-set above: once the cleared ("") value has
  // committed to the DOM, write the queued title so the text node transitions
  // ""→title and the region re-announces even when the title is unchanged.
  useEffect(() => {
    if (pendingAnnouncement === null) return;
    setRouteAnnouncement(pendingAnnouncement);
    setPendingAnnouncement(null);
  }, [pendingAnnouncement]);

  return (
    <div className={styles.appRoot}>
      {/* Header */}
      <header className={styles.header}>
        <h1 className={styles.brandHeading}>
          <button onClick={handleBackToCatalog} className={styles.logoButton}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={styles.logoIcon}
              aria-hidden="true"
              focusable="false"
            >
              <rect x="3" y="4" width="18" height="16" rx="3" />
              <circle cx="9" cy="12" r="1.5" />
              <circle cx="15" cy="12" r="1.5" />
              <path d="M9 16h6" />
              <path d="M2 10h-1" />
              <path d="M23 10h-1" />
            </svg>
            AI Marketplace
          </button>
        </h1>
        <div className={styles.headerActions}>
          <ThemeToggle />
          {user && (
            <span className={styles.userName}>
              {user.name || user.email}
            </span>
          )}
          <button onClick={handleLogout} className={styles.signOutButton}>
            Sign out
          </button>
        </div>
      </header>

      {/* Dev identity (shown in dev mode when no Entra user) */}
      {IS_DEV && !entraUser && (
        <div className={styles.devIdentity}>
          <strong>Dev identity:</strong>
          <input
            type="text"
            value={devName}
            onChange={(e) => setDevName(e.target.value)}
            placeholder="Name"
            className={`${styles.devInput} ${styles.devInputName}`}
          />
          <input
            type="email"
            value={devEmail}
            onChange={(e) => setDevEmail(e.target.value)}
            placeholder="Email"
            className={`${styles.devInput} ${styles.devInputEmail}`}
          />
        </div>
      )}

      {/* Polite live region: announces the new view to screen readers on
          navigation (paired with the focus move below). Visually hidden. */}
      <div role="status" aria-live="polite" className={styles.srOnly}>
        {routeAnnouncement}
      </div>

      {/* Main content. tabIndex={-1} makes it a programmatic focus target (not
          tab-reachable) so navigation can reposition keyboard/SR users here.
          styles.main shows a :focus-visible ring on that reposition so sighted
          keyboard users see where focus landed (mouse nav stays ring-free). */}
      <div className={styles.shell}>
        <SideNav route={route} isAdmin={isAdmin} mcpServersEnabled={mcpServersEnabled} onSelect={handleNavSelect} />

        <main
          ref={mainRef}
          tabIndex={-1}
          aria-label="Main content"
          className={styles.main}
        >
          {route.page === "catalog" && (
            <CatalogPage
              key={catalogNavKey}
              onSelectPlugin={handleSelectPlugin}
              initialClients={route.filterClients}
              initialCategories={route.filterCategories}
              initialQuery={route.filterQuery}
              initialType={route.filterType}
              initialOfficial={route.filterOfficial}
              initialIdentities={route.filterIdentities}
              onHelp={navigateToHelp}
              onFiltersChange={replaceFilters}
              aiRequested={aiRequested}
              setAiRequested={setAiRequested}
              aiResult={aiResult}
              setAiResult={setAiResult}
              aiResultQuery={aiResultQuery}
              setAiResultQuery={setAiResultQuery}
            />
          )}

          {route.page === "detail" && selectedPlugin && (
            <PluginDetailPage
              plugin={selectedPlugin}
              breadcrumbs={effectiveBreadcrumbs}
              parentPlugin={parentPlugin}
              dataThroughDate={adoptionMeta?.dataThroughDate}
              onBackToCatalog={handleBackToCatalog}
              onBreadcrumbClick={handleBreadcrumbClick}
              onNavigateToPlugin={(plugin) => {
                navigate({ page: "detail", installKey: installKeyForPlugin(plugin) });
              }}
              findPlugin={(installName) =>
                findByInstallKey(installName) ?? undefined
              }
              user={user}
              onEdit={() =>
                navigate({ page: "edit", installKey: installKeyForPlugin(selectedPlugin) })
              }
              onDelete={() => {
                removePlugin(installKeyForPlugin(selectedPlugin));
                handleBackToCatalog();
              }}
              onClientClick={(client) => {
                // These chips navigate straight to a NEW catalog query/filter (not via
                // handleBackToCatalog), so the lifted AI answer must be dropped here too — else it
                // would re-fire an unrequested paid judge call for a query the user never asked AI.
                resetAiAnswer();
                navigate({ page: "catalog", filterClients: [client] });
              }}
              onCategoryClick={(category) => {
                resetAiAnswer();
                navigate({ page: "catalog", filterCategories: [category] });
              }}
              onKeywordClick={(keyword) => {
                resetAiAnswer();
                navigate({ page: "catalog", filterQuery: keyword });
              }}
              onHelp={navigateToHelp}
            />
          )}

          {route.page === "detail" && !selectedPlugin && (
            // Don't gate on plugins.length: a deep-link (e.g. from My Skills) that
            // arrives while the catalog is still loading would otherwise render a
            // blank <main>. Show Loading until the catalog settles; a catalog FETCH
            // FAILURE gets its own message rather than "Not found" — the latter would
            // tell an owner their still-published skill was removed (a false "my skill
            // disappeared"), and My Skills is a new high-traffic source of these links.
            catalogLoading ? (
              <div className={styles.centeredState}>
                <p className={styles.stateText}>Loading...</p>
              </div>
            ) : catalogError ? (
              <div className={styles.centeredState}>
                <h2 className={styles.stateHeading}>Couldn&apos;t load the catalog</h2>
                <p className={styles.stateText}>
                  We couldn&apos;t load the marketplace catalog, so this page can&apos;t be shown. Please try again.
                </p>
                <button onClick={handleBackToCatalog} className={styles.backButton}>
                  Back to Marketplace
                </button>
              </div>
            ) : awaitingKey && route.installKey.toLowerCase() === awaitingKey.toLowerCase() ? (
              // Just published/edited but not in the loaded catalog yet: the processor
              // rebuilds marketplace.json asynchronously after the commit (issue #546).
              // Show a reassuring "processing" state with a manual refresh instead of a
              // false "Not found".
              <div className={styles.centeredState}>
                <h2 className={styles.stateHeading}>Your skill is being processed</h2>
                <p className={styles.stateText}>
                  It was submitted successfully and will appear in the catalog shortly. This can take up to a minute.
                </p>
                <div className={styles.stateActions}>
                  <button
                    onClick={async () => {
                      // Silent so a failure never flips the global catalog error; the
                      // boolean return drives scoped, local feedback instead (#546).
                      setRefreshFailed(false);
                      if (!(await refresh({ silent: true }))) setRefreshFailed(true);
                    }}
                    className={styles.backButton}
                  >
                    Refresh
                  </button>
                  <button onClick={handleBackToCatalog} className={styles.backButton}>
                    Back to Marketplace
                  </button>
                </div>
                {refreshFailed && (
                  <p role="alert" className={styles.stateError}>
                    We couldn&apos;t check for updates just now. Please try again in a moment.
                  </p>
                )}
              </div>
            ) : (
              <div className={styles.centeredState}>
                <h2 className={styles.stateHeading}>Not found</h2>
                <p className={styles.stateText}>
                  The plug-in or skill you&apos;re looking for doesn&apos;t exist or has been removed.
                </p>
                <button onClick={handleBackToCatalog} className={styles.backButton}>
                  Back to Marketplace
                </button>
              </div>
            )
          )}

          {route.page === "edit" && editLoading && (
            <div className={styles.centeredState}>
              <p className={styles.stateText}>Loading details...</p>
            </div>
          )}

          {route.page === "edit" && editError && (
            <div className={styles.centeredState}>
              <h2 className={styles.stateHeading}>Can't open the editor</h2>
              <p className={styles.stateText}>{editError}</p>
              <button onClick={handleEditBack} className={styles.backButton}>
                {editFromMyskills ? "Back to My Skills" : "Back to Marketplace"}
              </button>
            </div>
          )}

          {route.page === "edit" && editData && !editLoading && (
            <ContributeSkillPage
              onBack={handleEditBack}
              backLabel={editFromMyskills ? "Back to My Skills" : undefined}
              onViewSkill={handleViewSkill}
              user={user}
              editData={editData}
            />
          )}

          {route.page === "contribute" && (
            <ContributeSkillPage
              onBack={handleBackToCatalog}
              onViewSkill={handleViewSkill}
              user={user}
              catalogEntries={plugins}
            />
          )}

          {route.page === "help" && (
            <HelpPage
              key={helpNavKey}
              onBack={helpOrigin ? () => window.history.back() : handleBackToCatalog}
              backLabel={HELP_BACK_LABELS[helpOrigin ?? "catalog"]}
              onContribute={() => navigate({ page: "contribute" })}
              initialSection={route.section}
              onSectionOpen={handleHelpSectionOpen}
              topic={route.topic}
              onOpenMcpServers={() => navigate({ page: "mcp" })}
            />
          )}

          {route.page === "mcp" && (
            <McpServersPage
              key={mcpNavKey}
              initialQuery={route.filterQuery}
              initialSort={route.sort}
              onFiltersChange={replaceMcpFilters}
              onSelectServer={(serverName) => navigate({ page: "mcpserver", serverName })}
              onRequestHelp={() => navigateToMcpHelp("mcp-request")}
            />
          )}

          {route.page === "mcpserver" && (
            <McpServerDetailPage
              key={route.serverName}
              serverName={route.serverName}
              onBackToServers={() => navigate({ page: "mcp" })}
              onHelp={navigateToMcpHelp}
            />
          )}

          {route.page === "admin" && user && (
            <AdminPage onBack={handleBackToCatalog} />
          )}

          {route.page === "myskills" && (
            <MySkillsPage
              onOpenDetail={(installName) =>
                navigate({ page: "detail", installKey: installName })
              }
              onEdit={(installName) =>
                navigate({ page: "edit", installKey: installName, from: "myskills" })
              }
              onRecordDeleted={(installName) => removePlugin(installName)}
              onContribute={() => navigate({ page: "contribute" })}
              onHelp={navigateToHelp}
            />
          )}
        </main>
      </div>

      {/* Footer */}
      <footer className={styles.footer}>
        <p className={styles.footerText}>AI Marketplace © 2026 • Cox Automotive</p>
      </footer>
    </div>
  );
}
